/**
 * Animation utilities and constants for consistent motion design
 */

// Animation timing constants
export const ANIMATION_TIMING = {
  fast: 0.15,
  standard: 0.3,
  complex: 0.6,
  elaborate: 1.0,
  counter: 2.0,
} as const;

// Easing curves for different animation types
export const EASING = {
  easeOut: [0.0, 0.0, 0.2, 1.0] as const,
  easeInOut: [0.4, 0.0, 0.2, 1.0] as const,
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
} as const;

// Hero entrance animation sequence timings
export const HERO_ANIMATION_SEQUENCE = {
  headline: { delay: 0.0, duration: ANIMATION_TIMING.complex },
  subtext: { delay: 0.2, duration: ANIMATION_TIMING.elaborate },
  cta: { delay: 0.4, duration: ANIMATION_TIMING.standard },
  illustration: { delay: 0.3, duration: ANIMATION_TIMING.elaborate },
  trustIndicators: { delay: 0.6, duration: ANIMATION_TIMING.standard },
} as const;

// Animation variants for Framer Motion
export const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: ANIMATION_TIMING.complex,
      ease: EASING.easeOut,
    },
  },
};

export const slideUp = {
  initial: {
    opacity: 0,
    y: 50,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_TIMING.elaborate,
      ease: EASING.easeOut,
    },
  },
};

export const scaleIn = {
  initial: {
    opacity: 0,
    scale: 0.8,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_TIMING.standard,
      ease: EASING.bounce,
    },
  },
};

export const fadeInScale = {
  initial: {
    opacity: 0,
    scale: 0.9,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_TIMING.elaborate,
      ease: EASING.easeOut,
    },
  },
};

// Staggered animation for multiple elements
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const staggerItem = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_TIMING.standard,
      ease: EASING.easeOut,
    },
  },
};

// Hover animations for interactive elements
export const buttonHover = {
  scale: 1.05,
  transition: {
    duration: ANIMATION_TIMING.fast,
    ease: EASING.easeOut,
  },
};

export const buttonTap = {
  scale: 0.95,
  transition: {
    duration: 0.1,
    ease: EASING.easeOut,
  },
};

// Focus ring animation
export const focusRing = {
  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.5)',
  transition: {
    duration: ANIMATION_TIMING.fast,
    ease: EASING.easeOut,
  },
};

// Counter animation utility
export const createCounterAnimation = (
  from: number,
  to: number,
  duration: number = ANIMATION_TIMING.counter
) => ({
  from,
  to,
  transition: {
    duration,
    ease: EASING.easeOut,
  },
});

// Progress ring animation
export const progressRing = (progress: number) => ({
  strokeDashoffset: 283 - (283 * progress) / 100,
  transition: {
    duration: ANIMATION_TIMING.elaborate,
    ease: EASING.easeOut,
  },
});
